import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, PauseCircle, SkipForward, MapPin, ReceiptText, LogOut, LifeBuoy } from "lucide-react";
import Section, { PageHero } from "@/components/Section";
import Reveal from "@/components/Reveal";
import CTAButton from "@/components/CTAButton";
import { Field, TextInput, SelectInput, TextArea, FormNotice } from "@/components/FormControls";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { planFromId } from "@/lib/plans";
import { menuWeeks } from "@/data/menu";

const emptyChild = {
  name: "",
  age: "",
  age_group: "2-4",
  school: "",
  vegetarian: true,
  allergies: "",
  allergy_severity: "",
  restrictions: "",
  dislikes: "",
  spice_preference: "mild",
};

const inactiveFeatures = [
  { icon: PauseCircle, label: "Pause subscription" },
  { icon: SkipForward, label: "Skip a day" },
  { icon: MapPin, label: "Change delivery address" },
  { icon: ReceiptText, label: "Invoices and payment history" },
];

export default function AccountPage() {
  const brand = useBrand();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [children, setChildren] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(emptyChild);
  const [showForm, setShowForm] = useState(false);
  const [notice, setNotice] = useState({ tone: "info", message: "" });

  useEffect(() => {
    if (!loading && user === false) navigate("/sign-in", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/children").then((r) => setChildren(r.data)).catch(() => {});
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const addChild = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/children", form);
      setChildren((c) => [...c, data]);
      setForm(emptyChild);
      setShowForm(false);
      setNotice({ tone: "info", message: `${data.name}’s profile has been saved.` });
    } catch (err) {
      setNotice({
        tone: "error",
        message: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  const removeChild = async (id) => {
    await api.delete(`/children/${id}`);
    setChildren((c) => c.filter((x) => x.child_id !== id));
  };

  if (loading || !user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="font-heading text-sm font-semibold text-coralInk">Loading your account…</p>
      </div>
    );
  }

  const upcoming = menuWeeks[0].meals;

  return (
    <>
      <PageHero 
        eyebrow="My account"
        title={`Hello, ${user.name || "there"}.`}
        intro={`Your child profiles and subscription requests live here. ${brand.payments.note}`}
      >
        <div className="flex flex-wrap gap-3">
          <CTAButton to="/plans" data-testid="account-choose-plan">
            Choose a plan
          </CTAButton>
          <button
            type="button"
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            data-testid="account-logout"
            className="inline-flex min-h-[48px] items-center gap-2 rounded-control bg-cream px-5 text-sm font-bold text-cocoa transition-colors hover:bg-sageSoft"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </PageHero>

      <Section id="children" eyebrow="Child profiles" title="Who are we cooking for?">
        {notice.message && (
          <div className="mt-6 max-w-xl">
            <FormNotice tone={notice.tone} testId="account-notice">
              {notice.message}
            </FormNotice>
          </div>
        )}

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="children-list">
          {children.map((c) => (
            <div key={c.child_id} className="mbm-card p-6" data-testid={`child-card-${c.child_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-base font-semibold">{c.name}</h3>
                  <p className="mt-1 text-[13px] text-cocoa/70">
                    {brand.ageRanges.find((a) => a.id === c.age_group)?.label || c.age_group}
                    {c.age ? ` · ${c.age} years` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => removeChild(c.child_id)}
                  aria-label={`Remove ${c.name}`}
                  data-testid={`remove-child-${c.child_id}`}
                  className="text-cocoa/40 transition-colors hover:text-rust"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <dl className="mt-4 space-y-2 border-t border-line pt-4 text-[13px]">
                {[
                  ["School", c.school],
                  ["Diet", c.vegetarian ? "Vegetarian" : "Not vegetarian"],
                  ["Allergies", c.allergies || "None recorded"],
                  ["Restrictions", c.restrictions],
                  ["Dislikes", c.dislikes],
                  ["Spice", c.spice_preference],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <dt className="w-[86px] shrink-0 text-cocoa/70">{k}</dt>
                      <dd className="text-cocoa/70">{v}</dd>
                    </div>
                  ))}
              </dl>
            </div>
          ))}

          {!showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              data-testid="add-child-button"
              className="flex min-h-[180px] flex-col items-center justify-center gap-3 rounded-card border-2 border-dashed border-cocoa bg-surface/60 p-6 font-heading text-sm font-semibold text-coralInk transition-colors hover:border-forest/50"
            >
              <Plus className="h-5 w-5" />
              Add a child profile
            </button>
          )}
        </div>

        {showForm && (
          <form onSubmit={addChild} className="mbm-card mt-6 max-w-3xl p-6 sm:p-8" data-testid="child-form">
            <h3 className="font-heading text-lg font-semibold">Add a child</h3>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <Field label="Child’s name" required>
                <TextInput required value={form.name} onChange={set("name")} data-testid="child-name" />
              </Field>
              <Field label="Age">
                <TextInput value={form.age} onChange={set("age")} data-testid="child-age" />
              </Field>
              <Field label="Portion / age group" required>
                <SelectInput required value={form.age_group} onChange={set("age_group")} data-testid="child-age-group">
                  {brand.ageRanges.map((a) => (
                    <option key={a.id} value={a.id}>{`${a.label} — ${a.portion}`}</option>
                  ))}
                </SelectInput>
              </Field>
              <Field label="School">
                <TextInput value={form.school} onChange={set("school")} data-testid="child-school" />
              </Field>
              <Field label="Allergies" hint="Write ‘none’ if there are none." className="sm:col-span-2">
                <TextInput value={form.allergies} onChange={set("allergies")} data-testid="child-allergies" />
              </Field>
              <Field label="Allergy severity">
                <SelectInput value={form.allergy_severity} onChange={set("allergy_severity")} data-testid="child-allergy-severity">
                  <option value="">Not applicable</option>
                  <option value="mild">Mild</option>
                  <option value="moderate">Moderate</option>
                  <option value="severe">Severe</option>
                  <option value="life-threatening">Life-threatening</option>
                </SelectInput>
              </Field>
              <Field label="Spice preference">
                <SelectInput value={form.spice_preference} onChange={set("spice_preference")} data-testid="child-spice">
                  <option value="very-mild">Very mild</option>
                  <option value="mild">Mild</option>
                  <option value="medium">Medium</option>
                </SelectInput>
              </Field>
              <Field label="Dietary restrictions">
                <TextInput value={form.restrictions} onChange={set("restrictions")} data-testid="child-restrictions" />
              </Field>
              <Field label="Food dislikes">
                <TextInput value={form.dislikes} onChange={set("dislikes")} data-testid="child-dislikes" />
              </Field>
              <Field label="Anything else" className="sm:col-span-2">
                <TextArea rows={3} value={form.notes || ""} onChange={set("notes")} data-testid="child-notes" />
              </Field>
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <CTAButton type="submit" data-testid="child-submit">
                Save profile
              </CTAButton>
              <CTAButton type="button" variant="outline" onClick={() => setShowForm(false)} data-testid="child-cancel">
                Cancel
              </CTAButton>
            </div>
          </form>
        )}
      </Section>

      <Section id="subscriptions" tone="creamDeep" eyebrow="Subscriptions" title="Your requests">
        {orders.length === 0 ? (
          <div className="mt-8 max-w-xl">
            <FormNotice tone="info" testId="no-orders">
              You have not requested a subscription yet.{" "}
              <Link to="/plans" className="font-semibold underline">
                Choose a plan
              </Link>{" "}
              to get started.
            </FormNotice>
          </div>
        ) : (
          <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.order_id} className="mbm-card p-6">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-heading text-base font-semibold">
                    {planFromId(o.plan_id).shortName}
                  </h3>
                  <span className="rounded-full bg-amber/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-cocoa">
                    {o.status.replace(/_/g, " ")}
                  </span>
                </div>
                <ul className="mt-4 space-y-1.5 text-[13px] text-cocoa/70">
                  <li>Child: {o.child?.name}</li>
                  <li>Delivery: {o.delivery_mode} · {o.area} ({o.pincode})</li>
                  <li>Start date: {o.start_date || "to be confirmed"}</li>
                  <li>Allergy review: {o.allergy_review.replace(/_/g, " ")}</li>
                  <li>Payment: {o.payment_status.replace(/_/g, " ")}</li>
                </ul>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section id="upcoming" eyebrow="Upcoming meals" title="What is on the menu this week">
        <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="upcoming-meals">
          {upcoming.map((m) => (
            <Reveal as="li" key={m.day}>
              <div className="mbm-card h-full overflow-hidden">
                <img src={m.image} alt={m.name} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                <div className="p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-rust">{m.day}</p>
                  <p className="mt-1.5 text-sm font-medium leading-snug text-cocoa">{m.name}</p>
                  <p className="mt-2 text-[12px] text-cocoa/65">{m.nutrition.calories} kcal</p>
                </div>
              </div>
            </Reveal>
          ))}
        </ul>
        <p className="mt-5 text-xs text-cocoa/75">
          Shown from the published menu. Your personal delivery schedule appears here once a
          subscription is confirmed.
        </p>
      </Section>

      <Section id="coming-soon" tone="sage" eyebrow="Coming soon" title="Account features being built">
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {inactiveFeatures.map((f) => (
            <div
              key={f.label}
              className="flex items-center gap-3 rounded-card border-2 border-dashed border-cocoa bg-surface/60 p-5"
            >
              <f.icon className="h-5 w-5 shrink-0 text-coralInk" aria-hidden="true" />
              <p className="font-heading text-sm font-medium text-cocoa/70">{f.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex items-center gap-3 text-sm text-cocoa/70">
          <LifeBuoy className="h-4 w-4 text-coralInk" aria-hidden="true" />
          Until these are live, message us on WhatsApp and our team will do it for you.
        </div>
      </Section>
    </>
  );
}
